/**
 * Per-connection serialization lock for the Turso actor sync `Database`.
 *
 * Why this exists: the actor DB is the ONE writer connection when signed in (see
 * `open-actor-turso-sync-connection.ts`), and on that single connection the app's
 * write transactions and the sync engine's pull apply phase both open native
 * transactions on the underlying libsql replica:
 *   - drizzle implements `.transaction()` itself by issuing raw `begin` … `commit`
 *     SQL through the connection (it does NOT use the SDK's `transaction()`), and
 *   - `pull()` applies a remote batch via `applyChanges`, which opens its own
 *     write transaction inside the engine.
 *
 * `@tursodatabase/sync-react-native@0.6.1` does NOT mutually exclude these: its
 * internal `_execLock` is taken PER-STATEMENT (released between drizzle's `begin`,
 * the inserts, and `commit`), and `pull()` / `push()` take NO lock at all. So a
 * `begin` issued while a `pull()` is mid-`applyChanges` is rejected by the engine
 * ("cannot start a transaction within a transaction"), which drizzle surfaces as a
 * `DrizzleQueryError(query="begin")` — the user-facing "Could not save your
 * progress / Failed query: begin" toast.
 *
 * This lock closes that gap: every statement, every drizzle transaction (held
 * across `begin` … `commit`) and the APPLY phase of every `pull()`
 * acquire the SAME connection-scoped mutex, so a write transaction and a sync op can
 * never overlap. It also removes the related "a read interleaved with a sync op
 * panics the native engine" hazard the sync loop previously guarded against by hand.
 *
 * A pull's WAIT phase (`waitChanges()`, the HTTP round-trip) runs OUTSIDE the lock on
 * purpose — per the engine source it touches only the sync metadata and the
 * engine-owned `-changes` file, never the main connection. Holding the slot across it
 * queued every app read behind the network (~5s stalls on 50ms reads on staging).
 * See `open-actor-turso-sync-connection.ts`.
 * Native push uses the engine's separate CDC reader connections, so it also runs
 * outside this lock; `createLockedActorTursoSync` serializes sync metadata work
 * separately without making foreground queries wait for an HTTP upload.
 *
 * The lock is a single-slot FIFO built on `createBoundedSerialQueue`. It is REENTRANT
 * FOR TRANSACTION FRAMING ONLY: once `begin` has acquired the slot, the statements in
 * the transaction body run WITHOUT re-acquiring (they'd deadlock against the held
 * slot), and `commit` / `rollback` release it. Savepoints (drizzle's nested
 * transactions) run entirely inside an already-held slot, so they neither acquire
 * nor release.
 *
 * ## No wait on this lock can last forever
 *
 * It used to be a bare promise chain with no timeout, so one holder that never
 * released — a `begin` whose `commit` never came, an `applyChanges` that never
 * returned, a transaction callback that deadlocked by reading through the OUTER
 * handle (queueing behind its own slot) — parked every actor query in the app behind
 * it forever: a spinner with no error. Now:
 *
 *   - every acquirer waits in line for at most {@link LOCK_WAIT_DEADLINE_MS}, then is
 *     skipped (never run) and rejected with a transient `DeadlineExceededError`;
 *   - every holder has a HOLD deadline by kind ({@link HOLD_DEADLINE_MS}; for a
 *     transaction it covers the whole `begin` … `commit` span). A holder that outlives
 *     it POISONS the lock: every waiter and every later statement fails at once with
 *     `SerialQueuePoisonedError`, and `onDamaged` tells the owner, which abandons this
 *     connection and replaces it (`contexts/database/actor`'s lifecycle latches
 *     `failed`, whose recovery UI reopens).
 *
 * Poison, not "advance": the overrunning holder may still be inside the native engine
 * (or may still issue its `commit` later), and the next holder starting on the same
 * connection is exactly the interleaving this lock exists to prevent. And not "keep
 * the place until it settles": that is the forever-hang this replaced.
 */

import {
  boundedPromise,
  createBoundedSerialQueue,
  SerialQueuePoisonedError,
  unboundedWait,
  withDeadline,
} from "@-/dev.improve.haniverse.lib.deadline";
import { watchLockHolder, type LockHolderKind } from "#report-stuck-connection-lock";

/** Strip leading SQL comments/whitespace before classifying transaction control. */
const LEADING_SQL_COMMENTS_RE = /^\s*(?:\/\*[\s\S]*?\*\/\s*|--[^\n]*\n\s*)*/;
const BEGIN_TRANSACTION_RE = /^begin\b/;
const ROLLBACK_TRANSACTION_RE = /^rollback\b/;
const ROLLBACK_TO_SAVEPOINT_RE = /^rollback\s+to\b/;
const COMMIT_OR_END_RE = /^(?:commit|end)\b/;
const TRANSACTION_OWNER_STATEMENT_RE = /^(begin|commit|end|rollback|savepoint|release)\b/i;

/**
 * How long an operation may wait IN LINE for the slot before it is skipped and rejected.
 *
 * Matched to the central query backstop (`DeadlineBoundedQueryClient`, 30s): a foreground read
 * still queued after that has no awaiter left. Skipping a waiter is harmless to the connection —
 * it never ran — so this can be much tighter than the hold deadlines below, and a legitimately
 * long holder ahead (a large pull apply) costs the reads behind it a retry, not the connection.
 */
export const LOCK_WAIT_DEADLINE_MS = 30_000;

/**
 * How long each kind of holder may keep the slot before the lock is poisoned.
 *
 * These are "certainly wedged" thresholds, not "slow" ones — poisoning replaces the connection,
 * so a false positive costs the user a recovery screen. Every value is several times the
 * longest legitimate holder of its kind, and above the per-call native deadlines in
 * `open-owned-turso-generation.ts` so a native stall is reported by the layer that can name the
 * stuck engine call:
 *
 *   - `statement` 45s: one statement, including a restore batch statement or a migration DDL on
 *     a large table; the busy-retry budget inside it is 10s and the native per-call bound 30s.
 *   - `begin` 60s: the WHOLE `begin` … `commit` span or owned transaction callback — migration
 *     batches and the sign-up guest replay are the longest legitimate ones.
 *   - `sync-apply` / `sync-push` 150s: applying a large pulled batch (or, on web, a pull or push
 *     that holds the slot across its network round-trip) on a slow device; above the native
 *     `applyChanges` bound (120s).
 */
export const HOLD_DEADLINE_MS: Readonly<Record<LockHolderKind, number>> = {
  begin: 60_000,
  statement: 45_000,
  "sync-apply": 150_000,
  "sync-push": 150_000,
};

/**
 * Margin between the queue's own wait deadline and the acquire promise's backstop. The queue
 * always settles an acquirer first (granted or skipped); the backstop only exists so a bug in
 * that hand-off can never leave `acquire()` pending.
 */
const ACQUIRE_BACKSTOP_MARGIN_MS = 1000;

/** Short, stable label for errors and deadline reports: the kind plus the head of its SQL. */
function describeHolder(info: LockHolderInfo): string {
  if (info.queryText == null) {
    return info.kind;
  }
  const sql = info.queryText.replaceAll(/\s+/gu, " ").trim();
  return `${info.kind} ${sql.length > 60 ? `${sql.slice(0, 60)}…` : sql}`;
}

/** A function that releases a previously-acquired lock slot. Idempotent. */
export type LockRelease = () => void;

/**
 * Diagnostic description of WHY the slot is being taken. It feeds the stuck-slot
 * watchdog — supplying it is what turns "a database read is hanging" into "the
 * `begin` issued at <callsite> never got its commit, and 6 operations are queued
 * behind it" — and picks the hold deadline for the holder's kind.
 */
export type LockHolderInfo = {
  callsite?: string | null;
  /** Override the kind's hold deadline, for a holder known to be longer (a backup restore). */
  holdDeadlineMs?: number;
  kind: LockHolderKind;
  queryText?: string | null;
  /**
   * Override the wait-in-line deadline, for an operation that must not be skipped merely because a
   * legitimate long holder is ahead of it (closing the connection).
   */
  waitDeadlineMs?: number;
};

export interface ActorSyncConnectionLock {
  /**
   * Acquire the slot, returning a release function. Resolves once every
   * previously-queued acquirer has released, or rejects when the wait in line
   * exceeds {@link LOCK_WAIT_DEADLINE_MS} or the lock is damaged. Callers MUST call
   * the returned release exactly once (a `try`/`finally` is the safe idiom); a
   * holder that never releases poisons the lock at its hold deadline.
   */
  acquire(info?: LockHolderInfo): Promise<LockRelease>;
  readonly closed: boolean;
  /**
   * True once a holder overran its hold deadline: the connection may still be running that
   * holder's work, so nothing will ever run on it again. The owner replaces it.
   */
  readonly damaged: boolean;
  /** Recovery waits for uploads without blocking ordinary foreground statements. */
  drainBackgroundSync(): Promise<void>;
  readonly idle: boolean;
  /** Invalidate snapshot leases BEFORE closing or replacing the native handle. */
  invalidate(): void;
  /** Mark the connection unusable (a straggler may still be on it); fails every waiter now. */
  poison(cause: unknown): void;
  recoveryHold: boolean;
  /**
   * Run `op` while holding the slot; releases automatically (even on throw). Rejects with
   * `DeadlineExceededError` if `op` outlives its hold deadline (the lock is then poisoned).
   */
  runExclusive<T>(op: () => Promise<T>, info?: LockHolderInfo): Promise<T>;
  /** Track the actual native operation, which can outlive a caller's timeout. */
  trackBackgroundSync<T>(operation: Promise<T>): Promise<T>;
  /** Advances on every admitted operation, including sync and transaction BEGIN. */
  readonly version: number;
}

/**
 * How long recovery (`setBackupRecoveryHold`) waits for an in-flight upload to finish before
 * giving up with a retryable timeout. An upload still running after a minute is either huge or
 * wedged; either way the restore should report it rather than hold its screen.
 */
const BACKGROUND_SYNC_DRAIN_DEADLINE_MS = 60_000;

export function createActorSyncConnectionLock(
  options: {
    /** Names the connection in errors and reports. */
    name?: string;
    /**
     * Called once when the lock is poisoned — a holder overran and may still be using the
     * connection — so the owner can abandon it and open a replacement.
     */
    onDamaged?: (cause: unknown) => void;
  } = {},
): ActorSyncConnectionLock {
  const name = options.name ?? "Actor database connection";
  const queue = createBoundedSerialQueue({
    name,
    onOverrun: "poison",
    onPoisoned: (cause) => {
      options.onDamaged?.(cause);
    },
    // Per-holder deadlines are passed on every run; these are only the queue's defaults.
    runDeadline: HOLD_DEADLINE_MS.statement,
    waitDeadline: LOCK_WAIT_DEADLINE_MS,
  });
  let version = 0;
  let recoveryHold = false;
  let closed = false;
  const backgroundSync = new Set<Promise<unknown>>();

  // Counts queued acquirers and the current holder. Snapshot admission consults
  // this synchronously so queued work makes the connection busy immediately.
  let waiterCount = 0;

  /**
   * Queue `body` for the slot. `leave` must be called when the holder is done (the body's
   * settlement calls it too); it is what un-counts the holder synchronously on release, which
   * backup snapshot admission relies on.
   */
  function enqueue<T>(
    info: LockHolderInfo | undefined,
    body: (leave: () => void) => Promise<T>,
  ): Promise<T> {
    const holderInfo: LockHolderInfo = info ?? { kind: "statement" };
    waiterCount += 1;
    let counted = true;
    const leave = (): void => {
      if (counted) {
        counted = false;
        waiterCount -= 1;
      }
    };
    const holdDeadlineMs = holderInfo.holdDeadlineMs ?? HOLD_DEADLINE_MS[holderInfo.kind];
    return queue
      .run(
        describeHolder(holderInfo),
        async () => {
          if (closed) {
            throw new Error("Database connection has closed");
          }
          version += 1;
          // We now HOLD the slot. Arm the watchdog here rather than at queue time: a
          // long wait is a symptom of someone else's stuck holder, and reporting it too
          // would bury the one report that names the actual culprit.
          const disarm = watchLockHolder(() => ({
            callsite: holderInfo.callsite ?? null,
            kind: holderInfo.kind,
            queryText: holderInfo.queryText ?? null,
            // Exclude ourselves — report how many are BLOCKED by us.
            waiterCount: Math.max(0, waiterCount - (counted ? 1 : 0)),
          }));
          try {
            return await body(leave);
          } finally {
            disarm();
            leave();
          }
        },
        {
          runDeadline: holdDeadlineMs,
          waitDeadline: holderInfo.waitDeadlineMs ?? LOCK_WAIT_DEADLINE_MS,
        },
      )
      .catch((error: unknown) => {
        // Skipped in line, refused (closed/poisoned) or overran: no longer a waiter either way.
        leave();
        throw error;
      });
  }

  function acquire(info?: LockHolderInfo): Promise<LockRelease> {
    const label = `${name}: acquire ${info?.kind ?? "statement"}`;
    return boundedPromise<LockRelease>(
      {
        deadline: (info?.waitDeadlineMs ?? LOCK_WAIT_DEADLINE_MS) + ACQUIRE_BACKSTOP_MARGIN_MS,
        operation: label,
      },
      (grant, refuse, signal) => {
        enqueue(info, (leave) =>
          // The holder's slot: settles when the caller releases. Bounded by the queue, which
          // poisons the lock at the hold deadline — the only awaiter of this promise is the
          // queue's own (bounded) run, so it carries no deadline of its own.
          boundedPromise<void>(
            {
              deadline: unboundedWait(
                "a held connection slot; the lock queue's hold deadline bounds it and poisons the lock",
              ),
              operation: `${label} (held)`,
            },
            (released) => {
              if (signal.aborted) {
                // The acquirer already gave up (backstop fired): hand the slot straight back.
                leave();
                released();
                return;
              }
              // Idempotent, matching the documented contract of the release this wraps:
              // callers may invoke it more than once (the adapter's transaction framing
              // can release from either the commit frame or the begin frame's catch), and
              // a second call must not double-decrement the waiter count.
              let done = false;
              grant(() => {
                if (done) {
                  return;
                }
                done = true;
                leave();
                released();
              });
            },
          ),
        ).catch(refuse);
      },
    );
  }

  function runExclusive<T>(op: () => Promise<T>, info?: LockHolderInfo): Promise<T> {
    return enqueue(info, () => op());
  }

  return {
    acquire,
    get closed() {
      return closed;
    },
    get damaged() {
      return queue.poisoned;
    },
    drainBackgroundSync: async () => {
      if (backgroundSync.size === 0) {
        return;
      }
      await withDeadline(
        {
          deadline: BACKGROUND_SYNC_DRAIN_DEADLINE_MS,
          operation: `${name}: waiting for an in-flight upload to finish`,
        },
        () => Promise.allSettled(backgroundSync),
      );
    },
    get idle() {
      return waiterCount === 0 && !closed && !queue.poisoned;
    },
    invalidate() {
      closed = true;
      version += 1;
    },
    poison: (cause) => {
      queue.poison(cause);
    },
    get recoveryHold() {
      return recoveryHold;
    },
    set recoveryHold(value) {
      recoveryHold = value;
    },
    runExclusive,
    trackBackgroundSync: <T>(operation: Promise<T>): Promise<T> => {
      backgroundSync.add(operation);
      void operation.then(
        () => backgroundSync.delete(operation),
        () => backgroundSync.delete(operation),
      );
      return operation;
    },
    get version() {
      return version;
    },
  };
}

/** Whether `error` is the rejection of a lock (or sync queue) that was poisoned. */
export function isConnectionDamagedError(error: unknown): boolean {
  return error instanceof SerialQueuePoisonedError;
}

/**
 * Classify a SQL string as a transaction-control statement so the adapter can
 * frame drizzle's hand-rolled `begin` … `commit` under a single held lock slot.
 *
 * - `begin` (incl. `begin deferred` / `immediate` / `exclusive`) → "begin"
 * - `commit` / `end` → "commit"
 * - `rollback` (but NOT `rollback to savepoint …`, which is nested) → "rollback"
 * - everything else (incl. savepoints) → null
 */
/** Includes savepoints: SAVEPOINT outside a transaction can start one too. */
export function requiresTransactionOwner(sql: string): boolean {
  return TRANSACTION_OWNER_STATEMENT_RE.test(sql.replace(LEADING_SQL_COMMENTS_RE, "").trim());
}

export function classifyTransactionControl(sql: string): "begin" | "commit" | "rollback" | null {
  // Strip leading comments/whitespace the same way SQLite's tokenizer skips them,
  // then look at the first keyword. drizzle emits these as standalone statements.
  const normalized = sql.replace(LEADING_SQL_COMMENTS_RE, "").trim().toLowerCase();
  if (BEGIN_TRANSACTION_RE.test(normalized)) {
    return "begin";
  }
  // `rollback to savepoint …` is a nested-transaction control, NOT a top-level
  // release — it must stay inside the held slot, so don't classify it.
  if (ROLLBACK_TRANSACTION_RE.test(normalized) && !ROLLBACK_TO_SAVEPOINT_RE.test(normalized)) {
    return "rollback";
  }
  if (COMMIT_OR_END_RE.test(normalized)) {
    return "commit";
  }
  return null;
}
